import React, { useEffect, useRef, useState } from "react";
import LocalIcon from "./icons/LOCAL";
import EllipseIcon from "./icons/Ellipse";

const WhyChooseSection: React.FC = () => {
  const [connections, setConnections] = useState<
    Array<{ x1: number; y1: number; x2: number; y2: number }>
  >([]);
  const ellipseRefs = useRef<(HTMLDivElement | null)[]>([]);

  const features = [
    {
      title: "Empowering Decentralization",
      description:
        "LOCAL Protocol is redefining how marketplaces operate by eliminating the need for centralized oversight. We enable true peer-to-peer interactions, giving power back to the users.",
    },
    {
      title: "Adaptive and Resilient",
      description:
        "LOCAL protocol isn't one-size-fits-all; it's designed to adapt. Whether you're in a mature market or just starting out, LOCAL Protocol adjusts incentives to promote growth and fairness.",
    },
    {
      title: "Built for the Modern Economy",
      description:
        "In a world where markets are rapidly evolving, LOCAL provides the flexibility and interoperability that modern businesses demand. Create specialized marketplaces without the usual limitations.",
    },
    {
      title: "Secure and Trustworthy",
      description:
        "With built-in mechanisms to prevent fraud and encourage honest participation, LOCAL Protocol ensures that your transactions are secure. Our arbitration and reputation systems maintain integrity across the network.",
    },
    {
      title: "Join the Future of Marketplaces",
      description:
        "Become part of a network that not only values your contributions but also rewards you for them. LOCAL Protocol is more than a platform—it's a community-driven ecosystem designed for sustainable growth.",
    },
  ];

  useEffect(() => {
    const calculateConnections = () => {
      const newConnections: Array<{
        x1: number;
        y1: number;
        x2: number;
        y2: number;
      }> = [];

      const sectionElement = ellipseRefs.current[0]?.closest("section");
      if (!sectionElement) return;

      const sectionRect = sectionElement.getBoundingClientRect();

      ellipseRefs.current.forEach((element, index) => {
        if (!element) return;

        const rect1 = element.getBoundingClientRect();
        const x1 = rect1.left - sectionRect.left + rect1.width / 2;
        const y1 = rect1.top - sectionRect.top + rect1.height / 2;

        ellipseRefs.current.forEach((targetElement, targetIndex) => {
          if (targetIndex <= index) return;
          if (!targetElement) return;

          const rect2 = targetElement.getBoundingClientRect();
          const x2 = rect2.left - sectionRect.left + rect2.width / 2;
          const y2 = rect2.top - sectionRect.top + rect2.height / 2;

          newConnections.push({ x1, y1, x2, y2 });
        });
      });

      setConnections(newConnections);
    };

    setTimeout(calculateConnections, 100);
    window.addEventListener("resize", calculateConnections);
    window.addEventListener("scroll", calculateConnections);

    return () => {
      window.removeEventListener("resize", calculateConnections);
      window.removeEventListener("scroll", calculateConnections);
    };
  }, []);

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full">
        <div
          ref={(el) => (ellipseRefs.current[0] = el)}
          className="absolute top-20 -left-10 md:left-20"
        >
          <EllipseIcon
            className="w-40 h-40 opacity-70 animate-blob"
            fill="rgba(16, 185, 129, 0.1)"
            stroke="rgba(16, 185, 129, 0.4)"
          />
        </div>
        <div
          ref={(el) => (ellipseRefs.current[1] = el)}
          className="absolute top-40 -right-10 md:right-20"
        >
          <EllipseIcon
            className="w-40 h-40 opacity-70 animate-blob animation-delay-2000"
            fill="rgba(59, 130, 246, 0.1)"
            stroke="rgba(59, 130, 246, 0.4)"
          />
        </div>
        <div
          ref={(el) => (ellipseRefs.current[2] = el)}
          className="absolute bottom-20 -left-10 md:left-10"
        >
          <EllipseIcon
            className="w-40 h-40 opacity-90 animate-blob animation-delay-4000"
            fill="rgba(255, 255, 255, 0.1)"
          />
        </div>

        <svg className="absolute top-0 left-0 w-full h-full pointer-events-none">
          {connections.map((connection, index) => (
            <line
              key={index}
              x1={connection.x1}
              y1={connection.y1}
              x2={connection.x2}
              y2={connection.y2}
              stroke="rgba(16, 185, 129, 0.2)"
              strokeWidth="2"
              strokeDasharray="4"
              className="animate-dash"
            />
          ))}
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-medium text-gray-900 mb-6">
            Why Choose{" "}
            <LocalIcon className="relative bottom-[5px] w-[175px] h-[50px] text-local-blue inline-block" />
            ?
          </h2>
          <div className="w-24 h-1 bg-local-green mx-auto rounded-full"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group p-8 rounded-xl bg-white border border-gray-100 
                         hover:border-local-green transition-all duration-300 
                         hover:shadow-lg hover:-translate-y-1"
            >
              <h3 className="text-xl font-medium text-gray-900 mb-4 group-hover:text-local-green transition-colors">
                {feature.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
